import React, { FC, useEffect, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Header from '../../components/Header';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import {
  fetchPlxWerbeaktionConfig,
  fetchPlxProducts,
  savePlxWerbeaktionConfig,
  fetchPlxPriceHistory,
  savePlxPriceHistory,
  deletePlxPriceHistory,
  recachePlxOrderMetrics,
  recachePlxPaymentEvents,
  fetchUnmappedWKZs,
  fetchStubOrders,
  StubOrder,
  PlxWerbeaktionConfig,
  PlxProduct,
  PlxWerbeaktionConfigHistory,
  UnmappedWKZ,
} from '../../helpers/plxOrdersData';
import { tokens } from '../../theme';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  Add as AddIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const PlxOrdersConfigPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [products, setProducts] = useState<PlxProduct[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedWbkz, setSelectedWbkz] = useState<string>('');
  const [priceHistory, setPriceHistory] = useState<PlxWerbeaktionConfigHistory[]>([]);
  const [unmappedWKZs, setUnmappedWKZs] = useState<UnmappedWKZ[]>([]);
  const [unmappedExpanded, setUnmappedExpanded] = useState(false);
  const [stubOrders, setStubOrders] = useState<StubOrder[]>([]);
  const [stubsExpanded, setStubsExpanded] = useState(false);
  const [recachingMetrics, setRecachingMetrics] = useState(false);
  const [recachingPayments, setRecachingPayments] = useState(false);
  const [formData, setFormData] = useState({
    werbeaktion_wbkz: '',
    plx_product: '',
    quarterly_price: 0,
    is_upsell: false,
    is_one_time: false,
    is_cash_upfront: false,
    trial_days: 30,
  });
  const [displayPrice, setDisplayPrice] = useState(0);

  // State for adding new history entries
  const [addHistoryDialogOpen, setAddHistoryDialogOpen] = useState(false);
  const [historyFormData, setHistoryFormData] = useState({
    plx_product: '',
    quarterly_price: 0,
    is_upsell: false,
    is_one_time: false,
    is_cash_upfront: false,
    trial_days: 30,
    valid_from: '',
    valid_until: '',
  });
  const [historyDisplayPrice, setHistoryDisplayPrice] = useState(0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await fetchPlxProducts();
        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };

    const loadUnmappedWKZs = async () => {
      try {
        const data = await fetchUnmappedWKZs();
        setUnmappedWKZs(data);
      } catch (error) {
        console.error('Error loading unmapped WKZs:', error);
      }
    };

    const loadStubOrders = async () => {
      try {
        setStubOrders(await fetchStubOrders());
      } catch (error) {
        console.error('Error loading stub orders:', error);
      }
    };

    loadProducts();
    loadUnmappedWKZs();
    loadStubOrders();
  }, []);

  const headerMapping: Record<string, HeaderMappingItem> = {
    werbeaktion_wbkz: { name: 'Kampagnen-Code', type: 'string' },
    plx_product: { name: 'Produkt-Code', type: 'string' },
    product_name: { name: 'Produktname', type: 'string' },
    quarterly_price: { name: 'Preis/Quartal (€)', type: 'number', digits: 2 },
    is_upsell: { name: 'Upsell', type: 'boolean' },
    is_cash_upfront: { name: 'Cash-Up-Front', type: 'boolean' },
    trial_days: { name: 'Testphase (Tage)', type: 'number' },
    created_at: { name: 'Erstellt', type: 'date' },
    updated_at: { name: 'Aktualisiert', type: 'date' },
    price_history: {
      name: 'Preishistorie',
      type: 'custom',
      customRender: (werbeaktion_wbkz: string) => (
        <Button
          size="small"
          startIcon={<HistoryIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenHistory(werbeaktion_wbkz);
          }}
        >
          Historie
        </Button>
      ),
    },
  };

  const handleOpen = (config?: PlxWerbeaktionConfig) => {
    if (config) {
      const displayPriceValue = config.is_one_time
        ? config.quarterly_price
        : config.is_upsell
          ? config.quarterly_price * 4
          : config.quarterly_price;
      setFormData({
        werbeaktion_wbkz: config.werbeaktion_wbkz,
        plx_product: config.plx_product,
        quarterly_price: config.quarterly_price,
        is_upsell: config.is_upsell,
        is_one_time: config.is_one_time,
        is_cash_upfront: config.is_cash_upfront,
        trial_days: config.trial_days,
      });
      setDisplayPrice(displayPriceValue);
    } else {
      setFormData({
        werbeaktion_wbkz: '',
        plx_product: '',
        quarterly_price: 0,
        is_upsell: false,
        is_one_time: false,
        is_cash_upfront: false,
        trial_days: 30,
      });
      setDisplayPrice(0);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await savePlxWerbeaktionConfig(formData);
      handleClose();
      // Refresh unmapped WKZs data
      const data = await fetchUnmappedWKZs();
      setUnmappedWKZs(data);
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Fehler beim Speichern der Konfiguration');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHistory = async (werbeaktion_wbkz: string) => {
    setSelectedWbkz(werbeaktion_wbkz);
    setLoading(true);
    try {
      const history = await fetchPlxPriceHistory(werbeaktion_wbkz);
      setPriceHistory(history);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error loading price history:', error);
      alert('Fehler beim Laden der Preishistorie');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (rowData: PlxWerbeaktionConfig) => {
    handleOpen(rowData);
  };

  const handleQuickAdd = (unmappedWKZ: UnmappedWKZ) => {
    // Pre-fill form with WKZ and guess product from product_codes
    const firstProductCode = unmappedWKZ.product_codes.split(',')[0];
    setFormData({
      werbeaktion_wbkz: unmappedWKZ.werbeaktion_wbkz,
      plx_product: firstProductCode || '',
      quarterly_price: 0,
      is_upsell: false,
      is_one_time: false,
      is_cash_upfront: false,
      trial_days: 30,
    });
    setDisplayPrice(0);
    setDialogOpen(true);
  };

  const handleOpenAddHistory = () => {
    setHistoryFormData({
      plx_product: '',
      quarterly_price: 0,
      is_upsell: false,
      is_one_time: false,
      is_cash_upfront: false,
      trial_days: 30,
      valid_from: '',
      valid_until: '',
    });
    setHistoryDisplayPrice(0);
    setAddHistoryDialogOpen(true);
  };

  const handleCloseAddHistory = () => {
    setAddHistoryDialogOpen(false);
  };

  const handleSaveHistory = async () => {
    if (!selectedWbkz || !historyFormData.plx_product || !historyFormData.valid_from) {
      alert('Bitte füllen Sie alle Pflichtfelder aus: Produkt und Gültig von');
      return;
    }

    // Validate dates
    if (historyFormData.valid_until && historyFormData.valid_from > historyFormData.valid_until) {
      alert("'Gültig bis' muss nach 'Gültig von' liegen");
      return;
    }

    setLoading(true);
    try {
      await savePlxPriceHistory(selectedWbkz, {
        plx_product: historyFormData.plx_product,
        quarterly_price: historyFormData.quarterly_price,
        is_upsell: historyFormData.is_upsell,
        is_one_time: historyFormData.is_one_time,
        is_cash_upfront: historyFormData.is_cash_upfront,
        trial_days: historyFormData.trial_days,
        valid_from: historyFormData.valid_from,
        valid_until: historyFormData.valid_until || undefined,
      });

      // Refresh price history
      const history = await fetchPlxPriceHistory(selectedWbkz);
      setPriceHistory(history);

      handleCloseAddHistory();
      alert('Historischer Eintrag erfolgreich gespeichert');
    } catch (error: any) {
      console.error('Error saving history:', error);
      alert(`Fehler beim Speichern: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (id: number) => {
    if (!confirm('Möchten Sie diesen historischen Eintrag wirklich löschen?')) {
      return;
    }

    setLoading(true);
    try {
      await deletePlxPriceHistory(selectedWbkz, id);

      // Refresh price history
      const history = await fetchPlxPriceHistory(selectedWbkz);
      setPriceHistory(history);

      alert('Eintrag erfolgreich gelöscht');
    } catch (error: any) {
      console.error('Error deleting history:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecacheMetrics = async () => {
    if (
      !confirm('Möchten Sie die PLX Order Metrics neu berechnen? Dies kann 60-70 Sekunden dauern.')
    ) {
      return;
    }

    setRecachingMetrics(true);
    try {
      await recachePlxOrderMetrics();
      alert('PLX Order Metrics erfolgreich neu berechnet');
    } catch (error: any) {
      console.error('Error recaching metrics:', error);
      alert(`Fehler beim Neuberechnen: ${error.message}`);
    } finally {
      setRecachingMetrics(false);
    }
  };

  const handleRecachePayments = async () => {
    if (
      !confirm('Möchten Sie die PLX Payment Events neu berechnen? Dies kann 60-70 Sekunden dauern.')
    ) {
      return;
    }

    setRecachingPayments(true);
    try {
      await recachePlxPaymentEvents();
      alert('PLX Payment Events erfolgreich neu berechnet');
    } catch (error: any) {
      console.error('Error recaching payment events:', error);
      alert(`Fehler beim Neuberechnen: ${error.message}`);
    } finally {
      setRecachingPayments(false);
    }
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Anfo-Konfiguration"
          subtitle="Werbeaktion (Kampagnen) Konfiguration verwalten"
        />
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ height: '40px' }}
        >
          Neue Konfiguration
        </Button>
      </Box>

      {/* Instructions */}
      <Card sx={{ mb: 3, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Typography variant="h5" color={colors.greenAccent[500]} mb={2}>
            Anleitung
          </Typography>
          <Typography variant="body2" paragraph>
            Hier können Sie die Konfiguration für jede Werbeaktion (werbeaktion_wbkz) verwalten:
          </Typography>
          <Typography variant="body2" component="ul">
            <li>
              <strong>Kampagnen-Code:</strong> Der eindeutige werbeaktion_wbkz Code aus PLX
            </li>
            <li>
              <strong>Produkt:</strong> Das zugehörige Produkt aus der Produktliste
            </li>
            <li>
              <strong>Preis:</strong> Für normale Abos der Quartalspreis 📊, für Upsell-Produkte der
              Jahrespreis 📅, für Einmalfaktura der Einmalpreis 💶
            </li>
            <li>
              <strong>Upsell:</strong> Markieren Sie dies, wenn es sich um ein Upsell-Produkt
              handelt (Jahreszahlung mit jährlicher Verlängerung)
            </li>
            <li>
              <strong>Einmalfaktura:</strong> Markieren Sie dies, wenn es sich um eine einmalige
              Zahlung ohne Abonnement handelt (keine Verlängerung)
            </li>
            <li>
              <strong>Cash-Up-Front:</strong> Quartalsabo mit Sofortzahlung ohne Testphase
              (Cashback-Garantie statt Trial)
            </li>
            <li>
              <strong>Testphase:</strong> Anzahl der Tage für die Testphase (Standard: 30 Tage).
              <em>
                Gilt nur für normale Abos - Upsell, Einmalfaktura und Cash-Up-Front haben keine
                Testphase.
              </em>
            </li>
          </Typography>
          <Typography variant="body2" mt={2}>
            Klicken Sie auf eine Zeile in der Tabelle, um die Konfiguration zu bearbeiten.
          </Typography>
        </CardContent>
      </Card>

      {/* Unmapped WKZs Widget */}
      {unmappedWKZs.length > 0 && (
        <Card
          sx={{
            mb: 3,
            backgroundColor: colors.primary[400],
            border: `2px solid ${colors.redAccent[500]}`,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <WarningIcon sx={{ color: colors.redAccent[500], mr: 1 }} />
                <Typography variant="h5" color={colors.redAccent[500]}>
                  Fehlende WKZ-Konfigurationen ({unmappedWKZs.length} gefunden)
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setUnmappedExpanded(!unmappedExpanded)}
                endIcon={unmappedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {unmappedExpanded ? 'Weniger' : 'Details'}
              </Button>
            </Box>

            <Typography variant="body2" mb={2} color={colors.grey[100]}>
              Diese werbeaktion_wbkz Codes existieren in Bestellungen, haben aber keine
              Konfiguration für Preise und Produkte.
            </Typography>

            {unmappedExpanded && (
              <Box>
                {unmappedWKZs.slice(0, 10).map((wkz) => (
                  <Card
                    key={wkz.werbeaktion_wbkz}
                    sx={{ mb: 1, backgroundColor: colors.primary[500] }}
                  >
                    <CardContent sx={{ py: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="white">
                            {wkz.werbeaktion_wbkz}
                          </Typography>
                          <Typography variant="caption" color="white">
                            {wkz.order_count} Bestellungen • Produkt(e): {wkz.product_names}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleQuickAdd(wkz)}
                          startIcon={<AddIcon />}
                        >
                          Konfigurieren
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {unmappedWKZs.length > 10 && (
                  <Typography variant="caption" color={colors.grey[100]} mt={1}>
                    ... und {unmappedWKZs.length - 10} weitere
                  </Typography>
                )}
              </Box>
            )}

            {!unmappedExpanded && (
              <Typography variant="body2" color={colors.grey[100]}>
                Top WKZs:{' '}
                {unmappedWKZs
                  .slice(0, 3)
                  .map((w) => `${w.werbeaktion_wbkz} (${w.order_count})`)
                  .join(', ')}
                {unmappedWKZs.length > 3 && ` ... +${unmappedWKZs.length - 3} mehr`}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Unvollstaendige Bestellungen (Stubs) — zaehlen nirgends als Anfo */}
      {stubOrders.length > 0 && (
        <Card
          sx={{
            mb: 3,
            backgroundColor: colors.primary[400],
            border: `2px solid ${colors.redAccent[500]}`,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <WarningIcon sx={{ color: colors.redAccent[500], mr: 1 }} />
                <Typography variant="h5" color={colors.redAccent[500]}>
                  Unvollständige Bestellungen ({stubOrders.length})
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setStubsExpanded(!stubsExpanded)}
                endIcon={stubsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {stubsExpanded ? 'Weniger' : 'Details'}
              </Button>
            </Box>

            <Typography variant="body2" mb={2} color={colors.grey[100]}>
              Diese Bestellungen kamen von PL-X ohne Produkt, ohne Werbeaktion, ohne Kunde und ohne
              Zahlung. Wo der PX-Data-Import die Bestellung eindeutig wiederfindet, trägt er Produkt
              und WKZ vorläufig ein („vorläufig aus PX-Data“) — dann zählt sie als Anfo, aber nicht in
              Anfo-Wert und Conversion-Raten. Die übrigen zählen in keiner Auswertung. Bitte bei PL-X
              nachfragen, warum die Bestellung nicht vollständig ankommt.
            </Typography>

            {stubsExpanded && (
              <Box>
                {stubOrders.map((s) => (
                  <Card key={s.ordernumber} sx={{ mb: 1, backgroundColor: colors.primary[500] }}>
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="white">
                        {s.ordernumber} · {moment.utc(s.created_on).format('DD.MM.YYYY HH:mm')}
                        {s.kunde ? ` · Kunde ${s.kunde}` : ''}
                        {Number(s.provisional) === 1
                          ? ` · vorläufig aus PX-Data: ${s.px_artikel ?? '–'} / ${s.px_wkz ?? '–'}`
                          : ' · zählt nicht'}
                        {s.px_zahlart ? ` · Zahlart ${s.px_zahlart}` : ''}
                      </Typography>
                      <Typography variant="caption" color="white">
                        Banner: {s.banner || '–'}
                        {s.offerid ? ` · Angebot ${s.offerid}` : ''}
                        {s.offerurl ? ` · ${s.offerurl}` : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Table */}
      <Box
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <TableWidget<PlxWerbeaktionConfig>
          getData={fetchPlxWerbeaktionConfig}
          detailsHeaderMapping={headerMapping}
          initialSortColumn="werbeaktion_wbkz"
          searchByColumn="werbeaktion_wbkz"
          excelDownloadName="plx-werbeaktion-config"
          onRowClick={handleRowClick}
        />
      </Box>

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formData.werbeaktion_wbkz ? 'Konfiguration bearbeiten' : 'Neue Konfiguration erstellen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kampagnen-Code (werbeaktion_wbkz)"
                value={formData.werbeaktion_wbkz}
                onChange={(e) => setFormData({ ...formData, werbeaktion_wbkz: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Produkt</InputLabel>
                <Select
                  value={formData.plx_product}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      plx_product: e.target.value as string,
                    })
                  }
                  label="Produkt"
                >
                  <MenuItem value="">-- Bitte wählen --</MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.plx_product} value={product.plx_product}>
                      {product.name} ({product.plx_product})
                      {product.description && ` - ${product.description}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={
                  formData.is_one_time
                    ? '💶 Einmalpreis (€)'
                    : formData.is_upsell
                      ? '📅 Jahrespreis (€)'
                      : '📊 Preis pro Quartal (€)'
                }
                type="number"
                value={displayPrice}
                onChange={(e) => {
                  const newDisplayPrice = Number(e.target.value);
                  const newQuarterlyPrice = formData.is_one_time
                    ? newDisplayPrice
                    : formData.is_upsell
                      ? newDisplayPrice / 4
                      : newDisplayPrice;
                  setDisplayPrice(newDisplayPrice);
                  setFormData({
                    ...formData,
                    quarterly_price: newQuarterlyPrice,
                  });
                }}
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={
                  formData.is_upsell || formData.is_one_time || formData.is_cash_upfront
                    ? 'Testphase (nicht anwendbar)'
                    : 'Testphase (Tage)'
                }
                type="number"
                value={formData.trial_days}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trial_days: Number(e.target.value),
                  })
                }
                inputProps={{ min: 0 }}
                required={!formData.is_upsell && !formData.is_one_time && !formData.is_cash_upfront}
                disabled={formData.is_upsell || formData.is_one_time || formData.is_cash_upfront}
                helperText={
                  formData.is_cash_upfront
                    ? 'Cash-Up-Front hat keine Testphase'
                    : formData.is_one_time
                      ? 'Einmalfaktura hat keine Testphase'
                      : formData.is_upsell
                        ? 'Upsell-Produkte haben keine Testphase'
                        : 'Standard: 30 Tage'
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_upsell}
                    disabled={formData.is_one_time || formData.is_cash_upfront}
                    onChange={(e) => {
                      const newIsUpsell = e.target.checked;
                      let newDisplayPrice = displayPrice;
                      let newQuarterlyPrice = formData.quarterly_price;
                      let newTrialDays = formData.trial_days;

                      if (newIsUpsell && !formData.is_upsell) {
                        // Converting from quarterly to annual
                        newDisplayPrice = displayPrice * 4;
                        newQuarterlyPrice = displayPrice;
                        // Keep existing trial_days but field will be disabled
                      } else if (!newIsUpsell && formData.is_upsell) {
                        // Converting from annual to quarterly
                        newDisplayPrice = displayPrice / 4;
                        newQuarterlyPrice = displayPrice / 4;
                        // Reset to default if trial_days is 0 or very low (likely was disabled)
                        if (formData.trial_days < 10) {
                          newTrialDays = 30;
                        }
                      }

                      setDisplayPrice(newDisplayPrice);
                      setFormData({
                        ...formData,
                        is_upsell: newIsUpsell,
                        quarterly_price: newQuarterlyPrice,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="📅 Upsell-Produkt (Jahreszahlung ohne Testphase)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_one_time}
                    disabled={formData.is_upsell || formData.is_cash_upfront}
                    onChange={(e) => {
                      const newIsOneTime = e.target.checked;
                      let newTrialDays = formData.trial_days;

                      if (newIsOneTime) {
                        newTrialDays = 0;
                      } else if (!newIsOneTime && formData.trial_days === 0) {
                        newTrialDays = 30;
                      }

                      setFormData({
                        ...formData,
                        is_one_time: newIsOneTime,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="💶 Einmalfaktura (Einmalige Zahlung ohne Abo)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_cash_upfront}
                    disabled={formData.is_upsell || formData.is_one_time}
                    onChange={(e) => {
                      const newIsCashUpfront = e.target.checked;
                      let newTrialDays = formData.trial_days;

                      if (newIsCashUpfront) {
                        newTrialDays = 0;
                      } else if (!newIsCashUpfront && formData.trial_days === 0) {
                        newTrialDays = 30;
                      }

                      setFormData({
                        ...formData,
                        is_cash_upfront: newIsCashUpfront,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="💰 Cash-Up-Front (Sofortzahlung ohne Testphase)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading || !formData.werbeaktion_wbkz || !formData.plx_product}
          >
            {loading ? <LoadingSpinner /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preishistorie für {selectedWbkz}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {priceHistory.length === 0 ? (
              <Typography>Keine Preishistorie gefunden.</Typography>
            ) : (
              <Box>
                <Typography variant="h6" mb={2}>
                  Historische Preise:
                </Typography>
                {priceHistory.map((entry) => (
                  <Card key={entry.id} sx={{ mb: 2, backgroundColor: colors.primary[400] }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                          <Typography variant="body2">
                            <strong>Produkt:</strong> {entry.product_name} ({entry.plx_product})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Preis:</strong>{' '}
                            {entry.is_upsell
                              ? '📅 ' + (Number(entry.quarterly_price) * 4).toFixed(2)
                              : '📊 ' + Number(entry.quarterly_price).toFixed(2)}
                            €{entry.is_upsell ? ' (Jahrespreis)' : ' (pro Quartal)'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Testphase:</strong> {entry.trial_days} Tage
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Gültig von:</strong>{' '}
                            {new Date(entry.valid_from).toLocaleDateString('de-DE')}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Gültig bis:</strong>{' '}
                            {entry.valid_until
                              ? new Date(entry.valid_until).toLocaleDateString('de-DE')
                              : 'Aktuell'}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[100]}>
                            <strong>Erstellt:</strong>{' '}
                            {new Date(entry.created_at).toLocaleDateString('de-DE')} von{' '}
                            {entry.created_by}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteHistory(entry.id)}
                            disabled={loading}
                            title="Eintrag löschen"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <Box mt={3}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddHistory}
                fullWidth
              >
                Neuen historischen Eintrag hinzufügen
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={recachingMetrics ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRecacheMetrics}
              disabled={recachingMetrics || recachingPayments}
            >
              {recachingMetrics ? 'Berechne...' : 'Order Metrics neu berechnen'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={recachingPayments ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRecachePayments}
              disabled={recachingMetrics || recachingPayments}
            >
              {recachingPayments ? 'Berechne...' : 'Payment Events neu berechnen'}
            </Button>
          </Box>
          <Button onClick={() => setHistoryDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Add History Entry Dialog */}
      <Dialog open={addHistoryDialogOpen} onClose={handleCloseAddHistory} maxWidth="sm" fullWidth>
        <DialogTitle>Historischen Eintrag für {selectedWbkz} hinzufügen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Produkt</InputLabel>
                <Select
                  value={historyFormData.plx_product}
                  onChange={(e) =>
                    setHistoryFormData({
                      ...historyFormData,
                      plx_product: e.target.value as string,
                    })
                  }
                  label="Produkt"
                >
                  <MenuItem value="">-- Bitte wählen --</MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.plx_product} value={product.plx_product}>
                      {product.name} ({product.plx_product})
                      {product.description && ` - ${product.description}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={
                  historyFormData.is_one_time
                    ? '💶 Einmalpreis (€)'
                    : historyFormData.is_upsell
                      ? '📅 Jahrespreis (€)'
                      : '📊 Preis pro Quartal (€)'
                }
                type="number"
                value={historyDisplayPrice}
                onChange={(e) => {
                  const newDisplayPrice = Number(e.target.value);
                  const newQuarterlyPrice = historyFormData.is_one_time
                    ? newDisplayPrice
                    : historyFormData.is_upsell
                      ? newDisplayPrice / 4
                      : newDisplayPrice;
                  setHistoryDisplayPrice(newDisplayPrice);
                  setHistoryFormData({
                    ...historyFormData,
                    quarterly_price: newQuarterlyPrice,
                  });
                }}
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={
                  historyFormData.is_upsell ||
                  historyFormData.is_one_time ||
                  historyFormData.is_cash_upfront
                    ? 'Testphase (nicht anwendbar)'
                    : 'Testphase (Tage)'
                }
                type="number"
                value={historyFormData.trial_days}
                onChange={(e) =>
                  setHistoryFormData({
                    ...historyFormData,
                    trial_days: Number(e.target.value),
                  })
                }
                inputProps={{ min: 0 }}
                required={
                  !historyFormData.is_upsell &&
                  !historyFormData.is_one_time &&
                  !historyFormData.is_cash_upfront
                }
                disabled={
                  historyFormData.is_upsell ||
                  historyFormData.is_one_time ||
                  historyFormData.is_cash_upfront
                }
                helperText={
                  historyFormData.is_cash_upfront
                    ? 'Cash-Up-Front hat keine Testphase'
                    : historyFormData.is_one_time
                      ? 'Einmalfaktura hat keine Testphase'
                      : historyFormData.is_upsell
                        ? 'Upsell-Produkte haben keine Testphase'
                        : 'Standard: 30 Tage'
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={historyFormData.is_upsell}
                    disabled={historyFormData.is_one_time || historyFormData.is_cash_upfront}
                    onChange={(e) => {
                      const newIsUpsell = e.target.checked;
                      let newDisplayPrice = historyDisplayPrice;
                      let newQuarterlyPrice = historyFormData.quarterly_price;
                      let newTrialDays = historyFormData.trial_days;

                      if (newIsUpsell && !historyFormData.is_upsell) {
                        // Converting from quarterly to annual
                        newDisplayPrice = historyDisplayPrice * 4;
                        newQuarterlyPrice = historyDisplayPrice;
                      } else if (!newIsUpsell && historyFormData.is_upsell) {
                        // Converting from annual to quarterly
                        newDisplayPrice = historyDisplayPrice / 4;
                        newQuarterlyPrice = historyDisplayPrice / 4;
                        if (historyFormData.trial_days < 10) {
                          newTrialDays = 30;
                        }
                      }

                      setHistoryDisplayPrice(newDisplayPrice);
                      setHistoryFormData({
                        ...historyFormData,
                        is_upsell: newIsUpsell,
                        quarterly_price: newQuarterlyPrice,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="📅 Upsell-Produkt (Jahreszahlung ohne Testphase)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={historyFormData.is_one_time}
                    disabled={historyFormData.is_upsell || historyFormData.is_cash_upfront}
                    onChange={(e) => {
                      const newIsOneTime = e.target.checked;
                      let newTrialDays = historyFormData.trial_days;

                      if (newIsOneTime) {
                        newTrialDays = 0;
                      } else if (!newIsOneTime && historyFormData.trial_days === 0) {
                        newTrialDays = 30;
                      }

                      setHistoryFormData({
                        ...historyFormData,
                        is_one_time: newIsOneTime,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="💶 Einmalfaktura (Einmalige Zahlung ohne Abo)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={historyFormData.is_cash_upfront}
                    disabled={historyFormData.is_upsell || historyFormData.is_one_time}
                    onChange={(e) => {
                      const newIsCashUpfront = e.target.checked;
                      let newTrialDays = historyFormData.trial_days;

                      if (newIsCashUpfront) {
                        newTrialDays = 0;
                      } else if (!newIsCashUpfront && historyFormData.trial_days === 0) {
                        newTrialDays = 30;
                      }

                      setHistoryFormData({
                        ...historyFormData,
                        is_cash_upfront: newIsCashUpfront,
                        trial_days: newTrialDays,
                      });
                    }}
                  />
                }
                label="💰 Cash-Up-Front (Sofortzahlung ohne Testphase)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gültig von"
                type="date"
                value={historyFormData.valid_from}
                onChange={(e) =>
                  setHistoryFormData({
                    ...historyFormData,
                    valid_from: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gültig bis"
                type="date"
                value={historyFormData.valid_until}
                onChange={(e) =>
                  setHistoryFormData({
                    ...historyFormData,
                    valid_until: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                helperText="Leer lassen für aktuell gültig"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddHistory}>Abbrechen</Button>
          <Button
            onClick={handleSaveHistory}
            variant="contained"
            color="primary"
            disabled={loading || !historyFormData.plx_product || !historyFormData.valid_from}
          >
            {loading ? <LoadingSpinner /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlxOrdersConfigPage;
